import { useCallback, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useDeleteHouseholdMutation } from '@/modules/household/graphql/Households.generated';

interface DeleteHouseholdButtonProps {
  householdId: string;
  onDelete: () => void;
}

export function DeleteHouseholdButton({
  householdId,
  onDelete,
}: DeleteHouseholdButtonProps) {
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [deleteHousehold, { loading }] = useDeleteHouseholdMutation({
    onCompleted: () => {
      showFeedback('Client deleted successfully', { variant: 'success' });
      onDelete();
      setIsModalOpen(false);
    },
    onError: (error) => {
      showFeedback('Failed to delete household');
      reportError('Failed to delete household', error);
      setIsModalOpen(false);
    },
    update(cache) {
      // Evict the deleted household
      cache.evict({
        id: cache.identify({
          __typename: 'Household',
          id: householdId,
        }),
      });
    },
  });

  const handleDelete = useCallback(() => {
    void deleteHousehold({ variables: { id: householdId } });
  }, [deleteHousehold, householdId]);

  return (
    <>
      <Button
        variant="destructive-secondary"
        size="sm"
        data-testid="DeleteHouseholdButton"
        onClick={() => setIsModalOpen(true)}
      >
        Delete client
      </Button>
      <ConfirmationModal
        isOpen={isModalOpen}
        heading="Delete client"
        onClose={(reason) => {
          if (reason === 'confirm') {
            handleDelete();
          } else {
            setIsModalOpen(false);
          }
        }}
        confirmText="Delete"
        confirmButtonProps={{
          variant: 'destructive-primary',
          loading,
        }}
      >
        Are you sure you want to delete this client? This action is
        irreversible.
      </ConfirmationModal>
    </>
  );
}
